import { useCallback, useEffect, useState } from "react";
import { FormattedMessage, injectIntl, WrappedComponentProps } from "react-intl";
import { connect, ConnectedProps } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import ConfirmModal from "../../../../../components/confirm-modal";
import ErrorMessage from "../../../../../components/error-message";
import UpInput from "../../../../../components/up-input";
import UpInputWithLabel from "../../../../../components/UpInputWithLabel";
import { RootReduxState } from "../../../../../utils/types";
import {
  BrevoRequestReduxState,
  getBrevoIntegrationSettingsAction,
  resetBrevoIntegrationsRequestsAction,
  setupBrevoIntegrationAction
} from "./redux";

type BrevoConnectModalComponentOwnProps = {
  handleClose: () => void;
};

const mapStateToProps = (state: RootReduxState) => {
  const selectedShopId = state.getIn([
    "customerBackendDashboard",
    "userData",
    "selectedShopId"
  ]) as string;

  const setupRequestState = state
    .getIn(["integrations", "brevo", "requests", "setup"])
    .toJS() as BrevoRequestReduxState;

  return {
    shopId: Number(selectedShopId),
    setupRequestState
  };
};
const matchDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      getBrevoIntegrationSettingsAction,
      setupBrevoIntegrationAction,
      resetBrevoIntegrationsRequestsAction
    },
    dispatch
  );
const connector = connect(mapStateToProps, matchDispatchToProps);

type BrevoConnectModalComponentProps = BrevoConnectModalComponentOwnProps &
  ConnectedProps<typeof connector>;

const BrevoConnectModalComponent = (
  props: BrevoConnectModalComponentProps & WrappedComponentProps
) => {
  const {
    handleClose,
    intl,
    setupBrevoIntegrationAction,
    getBrevoIntegrationSettingsAction,
    resetBrevoIntegrationsRequestsAction,
    shopId,
    setupRequestState
  } = props;

  const [apiKey, setApiKey] = useState("");
  const [isSubmittable, setIsSubmittable] = useState(false);

  useEffect(
    () => () => {
      resetBrevoIntegrationsRequestsAction();
    },
    []
  );

  useEffect(() => {
    if (setupRequestState.success) {
      getBrevoIntegrationSettingsAction(shopId);
      handleClose();
    }
  }, [setupRequestState.success]);

  useEffect(() => {
    setIsSubmittable(apiKey.trim().length > 0);
  }, [apiKey]);

  const handleSubmit = useCallback(() => {
    setupBrevoIntegrationAction(shopId, apiKey);
  }, [shopId, apiKey]);

  const handleApiKeyChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setApiKey(e.target.value);
  }, []);

  const getErrorMessage = useCallback(() => {
    switch (setupRequestState.error) {
      case "invalid-key": {
        return intl.formatMessage({
          id: "containers.Integrations.brevo.popup.errors.invalidKey",
          defaultMessage:
            'Please verify your API key. If the key is valid but connections are still blocked, go to: Account menu → Security → Authorized IPs → disable "Block unknown IP addresses."'
        });
      }
      case "unknown-error": {
        return intl.formatMessage({
          id: "containers.Integrations.brevo.popup.errors.other",
          defaultMessage: "Error connecting Brevo"
        });
      }
      default: {
        return null;
      }
    }
  }, [setupRequestState.error, intl]);

  return (
    <ConfirmModal
      title={
        <FormattedMessage
          id="containers.Integrations.brevo.popup.headline"
          defaultMessage="Connect Account"
        />
      }
      leftButtonOnClick={handleClose}
      rightButtonOnClick={handleSubmit}
      leftButtonText={
        <FormattedMessage
          id="containers.Integrations.brevo.popup.button.cancel"
          defaultMessage="Cancel"
        />
      }
      rightButtonText={
        <FormattedMessage
          id="containers.Integrations.brevo.popup.button.connect"
          defaultMessage="Connect"
        />
      }
      rightButtonDisabled={!isSubmittable}
      close={handleClose}
      loading={setupRequestState.loading ?? false}
      visible
    >
      <span style={{ fontWeight: 500 }}>
        <FormattedMessage
          id={`containers.Integrations.brevo.popup.description`}
          defaultMessage="Please enter your Brevo API Key."
        />
      </span>
      <UpInputWithLabel
        label={intl.formatMessage({
          id: "containers.Integrations.brevo.popup.input.label",
          defaultMessage: "Your Brevo API Key"
        })}
        margin="10px 0 10px 0"
        width="100%"
        flex="1 1"
      >
        <UpInput
          value={apiKey}
          onChange={handleApiKeyChange}
          width="100%"
          type="text"
          placeholder={intl.formatMessage({
            id: "containers.Integrations.brevo.popup.input.placeholder",
            defaultMessage: "API Key"
          })}
        />
      </UpInputWithLabel>
      <ErrorMessage>{getErrorMessage()}</ErrorMessage>
    </ConfirmModal>
  );
};

export const BrevoConnectModal = connector(injectIntl(BrevoConnectModalComponent));
